"use client";

import { capitalize, debounce } from "lodash";
import { Check, ChevronsUpDown } from "lucide-react";
import Image from "next/image";
import * as React from "react";
import { twMerge } from "tailwind-merge";

import { AssetType } from "@/components/global/seachDialog";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { usePathname } from "@/lib/i18n";
import { cn, formatNumber, textColorBasedOnNumber } from "@/lib/utils";
import * as m from "@/paraglide/messages";
import { searchAssets } from "@/services/controllers/search";

export function CoinsDropdown({
  onSelect,
  defaultCoin,
}: {
  onSelect: React.Dispatch<React.SetStateAction<AssetType | undefined>>;
  defaultCoin?: AssetType;
}) {
  const pathname = usePathname();

  const [open, setOpen] = React.useState<boolean>(false);
  const [value, setValue] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(true);
  const [assets, setAssets] = React.useState<AssetType[]>([]);
  const [selected, setSelected] = React.useState<string | undefined>();

  React.useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    if (defaultCoin != null && pathname.includes("/asset")) {
      setSelected(defaultCoin.name);
    } else {
      searchAssets({
        signal: controller.signal,
        params: {
          search: value ?? "",
          limit: 100,
          offsit: 0,
        },
      })
        .then(({ data: { data } }) => {
          if (Array.isArray(data)) {
            let arr: AssetType[] = [];
            arr = [
              ...data
                .filter((e) => e.asset_data.image.startsWith("https://"))
                .map((asset) => ({
                  id: asset?.id ?? 0,
                  name: asset.asset_data.name,
                  currentPrice: asset.asset_data.current_price,
                  pnl: asset.asset_data.price_change_percentage_24h?.toFixed(2),
                  price: asset.current_price ?? 0,
                  rank: asset.asset_data.market_cap_rank,
                  volume: asset.asset_data.total_volume,
                  iconUrl: asset.asset_data.image,
                  mrkCap: asset.asset_data.market_cap,
                  symbol: asset.asset_data.symbol.toLowerCase(),
                  isFavorite: asset?.is_favorite ?? false,
                  change24H: asset.asset_data.price_change_percentage_24h,
                  change7D:
                    asset.asset_data.price_change_percentage_7d_in_currency,
                  change14D:
                    asset.asset_data.price_change_percentage_14d_in_currency,
                  exposureName: asset.asset_name.toUpperCase(),
                })),
            ];
            setAssets(arr);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }

    return () => {
      controller.abort();
    };
  }, [value, defaultCoin, pathname]);

  const handleSearch = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const term = e.target.value;
      setValue(term);
    },
    [],
  );

  const debouncedSearch = React.useMemo(
    () => debounce(handleSearch, 250),
    [handleSearch],
  );

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          id="coin"
          variant="outline"
          className="flex items-center px-2 h-[36px] shadow-sm w-full justify-between font-normal"
          disabled={defaultCoin != null && pathname.includes("/asset")}
        >
          {capitalize(selected ?? m.fit_stout_poodle_catch())}
          <ChevronsUpDown className="h-3 w-3 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0">
        <Command>
          <Input
            id="coins-search-input"
            placeholder={m.search_coin()}
            onChange={debouncedSearch}
            className="w-full rounded-sm shadow-none border-0 border-b-2 focus-visible:ring-0"
          />
          <CommandList>
            <CommandGroup className="md:w-[350px] w-full">
              {loading ? (
                <div className="flex flex-col gap-2">
                  {[...Array(5)].map(() => (
                    <div className="flex justify-between" key={Math.random()}>
                      <div className="flex items-center gap-1">
                        <Skeleton className="mr-2 h-4 w-4" />
                        <Skeleton className="mr-1 h-[23px] w-[23px]" />
                        <Skeleton className="w-32 h-[23px]" />
                      </div>

                      <Skeleton className="w-20 h-auto" />
                    </div>
                  ))}
                </div>
              ) : (
                assets.map((coin) => (
                  <CommandItem
                    key={coin.id}
                    value={coin.name.toUpperCase()}
                    onSelect={(currentValue) => {
                      setSelected(currentValue);
                      onSelect(coin);
                      setOpen(false);
                    }}
                    className="flex justify-between"
                  >
                    <div className="flex items-center gap-1">
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selected?.toLowerCase() == coin.name.toLowerCase()
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                      />
                      <Image
                        alt={coin.exposureName}
                        src={coin.iconUrl ?? "/placeholder.svg"}
                        width={24}
                        height={24}
                        className="mr-1 rounded-full"
                        key={coin.id}
                      />
                      <span className="text-xs">
                        {coin.name} &#x2022; <b>{coin.exposureName}</b>
                      </span>
                    </div>

                    <p
                      className={twMerge(
                        textColorBasedOnNumber(coin.change24H),
                        "text-xs",
                      )}
                    >
                      {formatNumber(coin.currentPrice, true, false)}
                    </p>
                  </CommandItem>
                ))
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
